import { Fragment, useEffect, useState, type ReactNode } from 'react';
import {
  Collapse,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Folder as FolderIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  History as HistoryIcon,
  FactCheck as FactCheckIcon,
  FolderOpen as RestoreIcon,
  Lock as PasswordIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  GroupWork as GroupWorkIcon,
  Sensors as SensorsIcon,
  Hub as HubIcon,
  NotificationsActive as AlertIcon,
  AdminPanelSettings as RolePermissionsIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { navSections, type NavIcon, type NavItem } from '@/config/navigation';
import VersionBadge from '@/components/VersionBadge';
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH, readOpenGroups, writeOpenGroups } from './sidebarState';

interface SidebarProps {
  mobileOpen?: boolean;
  onClose?: () => void;
  /** Desktop drawer shows icons only; Layout owns and remembers the state. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

// The navigation table lives in src/config/navigation.ts (shared with the
// Playwright role-matrix spec, ADR-043 G-5); this only maps icon keys.
const icons: Record<NavIcon, ReactNode> = {
  assessment: <AssessmentIcon />,
  folder: <FolderIcon />,
  history: <HistoryIcon />,
  factCheck: <FactCheckIcon />,
  storage: <StorageIcon />,
  hub: <HubIcon />,
  schedule: <ScheduleIcon />,
  restore: <RestoreIcon />,
  sensors: <SensorsIcon />,
  people: <PeopleIcon />,
  groupWork: <GroupWorkIcon />,
  password: <PasswordIcon />,
  alert: <AlertIcon />,
  rolePermissions: <RolePermissionsIcon />,
};

const selectedGradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

export default function Sidebar({ mobileOpen, onClose, collapsed = false, onToggleCollapsed }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const user = useAuthStore((s) => s.user);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onClose) {
      onClose();
    }
  };

  // Sections and the role filter come from src/config/navigation.ts; a group
  // with nothing visible for this role has no header.
  const sections = navSections(user?.role);

  // Groups start collapsed and open on click; the choice is remembered. The
  // group holding the current page opens on arrival so the selected item is
  // never hidden behind a collapsed header.
  const activeGroup = sections.find((s) => s.key && s.items.some((i) => i.path === location.pathname))?.key;
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(() => {
    const stored = readOpenGroups();
    if (activeGroup) stored.add(activeGroup);
    return stored;
  });

  useEffect(() => {
    if (!activeGroup) return;
    setOpenGroups((prev) => {
      if (prev.has(activeGroup)) return prev;
      const next = new Set(prev).add(activeGroup);
      writeOpenGroups(next);
      return next;
    });
  }, [activeGroup]);

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      writeOpenGroups(next);
      return next;
    });
  };

  const renderItem = (item: NavItem, mini: boolean) => {
    const button = (
      <ListItemButton
        selected={location.pathname === item.path}
        onClick={() => handleNavigation(item.path)}
        aria-label={mini ? item.text : undefined}
        sx={{
          mx: 1,
          mb: 0.5,
          borderRadius: 2,
          justifyContent: mini ? 'center' : 'flex-start',
          px: mini ? 0 : 2,
          '&.Mui-selected': {
            background: selectedGradient,
            color: 'white',
            '& .MuiListItemIcon-root': {
              color: 'white',
            },
            '&:hover': {
              background: selectedGradient,
            },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: mini ? 0 : 40 }}>{icons[item.icon]}</ListItemIcon>
        {!mini && <ListItemText primary={item.text} />}
      </ListItemButton>
    );
    return (
      <ListItem key={item.text} disablePadding>
        {mini ? (
          <Tooltip title={item.text} placement="right">
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    );
  };

  const renderGroup = (key: string, label: string, items: NavItem[], idPrefix: string) => {
    const open = openGroups.has(key);
    const regionId = `${idPrefix}-nav-group-${key}`;
    return (
      <Fragment key={key}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => toggleGroup(key)}
            aria-expanded={open}
            aria-controls={regionId}
            sx={{ mx: 1, mt: 0.5, mb: 0.5, py: 0.5, borderRadius: 2 }}
          >
            <ListItemText
              disableTypography
              primary={<Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>}
            />
            {open ? (
              <ExpandLess fontSize="small" sx={{ color: 'text.secondary' }} />
            ) : (
              <ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={open} timeout={reduceMotion ? 0 : 'auto'} component="li" id={regionId}>
          <List disablePadding>{items.map((item) => renderItem(item, false))}</List>
        </Collapse>
      </Fragment>
    );
  };

  // `mini` draws the icon-only rail: every item shows, groups are separated by
  // dividers and cannot be collapsed. `withToggle` adds the rail control, which
  // only the desktop drawer has. `idPrefix` keeps the two drawers' ids apart.
  const renderDrawer = (mini: boolean, withToggle: boolean, idPrefix: string) => (
    <>
      <Toolbar />
      {!mini && (
        <Box sx={{ p: 3, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
            備份系統
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            碎形分持加密
          </Typography>
        </Box>
      )}
      {/* The drawer paper is a flex column; the list takes the free height so
          the footer stays pinned to the bottom. */}
      <List sx={{ pt: 1, flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {sections.map((section) => {
          if (!section.key || !section.label) {
            return <Fragment key="top">{section.items.map((item) => renderItem(item, mini))}</Fragment>;
          }
          if (mini) {
            return (
              <Fragment key={section.key}>
                <Divider component="li" sx={{ mx: 1.5, my: 1 }} />
                {section.items.map((item) => renderItem(item, true))}
              </Fragment>
            );
          }
          return renderGroup(section.key, section.label, section.items, idPrefix);
        })}
      </List>
      <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.12)' }}>
        {withToggle && onToggleCollapsed && (
          <Tooltip title={mini ? '展開側欄' : ''} placement="right">
            <ListItemButton
              onClick={onToggleCollapsed}
              aria-label={mini ? '展開側欄' : '收合側欄'}
              sx={{
                mx: 1,
                my: 0.5,
                borderRadius: 2,
                justifyContent: mini ? 'center' : 'flex-start',
                px: mini ? 0 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: mini ? 0 : 40 }}>{mini ? <ExpandIcon /> : <CollapseIcon />}</ListItemIcon>
              {!mini && (
                <ListItemText
                  disableTypography
                  primary={
                    <Typography variant="body2" color="text.secondary">
                      收合側欄
                    </Typography>
                  }
                />
              )}
            </ListItemButton>
          </Tooltip>
        )}
        {!mini && (
          <Box sx={{ px: 3, pt: withToggle ? 0 : 1.5, pb: 1.5 }}>
            <VersionBadge />
          </Box>
        )}
      </Box>
    </>
  );

  const desktopWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  const widthTransition = theme.transitions.create('width', { duration: theme.transitions.duration.shorter });

  return (
    <>
      {/* Mobile drawer: always full width with labels */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: SIDEBAR_WIDTH,
          },
        }}
      >
        {renderDrawer(false, false, 'mobile')}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: desktopWidth,
          flexShrink: 0,
          transition: widthTransition,
          '& .MuiDrawer-paper': {
            width: desktopWidth,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: widthTransition,
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '& .MuiDrawer-paper': { transition: 'none' },
          },
        }}
      >
        {renderDrawer(collapsed, true, 'desktop')}
      </Drawer>
    </>
  );
}
